/* ConfigViewRenderer.js */

"use strict";
const _ = require("lodash");
const ComboBox = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ComboBox;
const FileName = require("@gluenetworks/steve_utilities").fileName;

class ConfigViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("config-view-gl");
        this._rootElem.tabIndex = -1;
        this._rootElem.component = this.model;
        this._defaultViewActive = false;
        this._viewActionsAdded = false;
        this._optionsComboBox = new ComboBox();
        this._model._gameEditor._renderer._rootElem.classList.add("config-view-game-editor");

        this.snapshotTypeComboBox = new ComboBox({
            adjustWidth: false,
            options: [
                {value: "drift_n_audit", label: "Configuration"},
                {value: "audit_only", label: "Operational"},
            ],
            value: "drift_n_audit",
            required: true,
        });

        this._optionsSnapshotLabelElem = document.createElement("options-label-gl");
        this._optionsSnapshotLabelElem.classList.add("hidden");

        this._optionsLatestLabelElem = document.createElement("options-label-gl");
        this._optionsContainerElem = document.createElement("options-container-gl");
        this._optionsContainerElem.classList.add("hidden");
        this.typeContainerElem = document.createElement("icon-container-gl");
        this.typeIconElem = document.createElement("base-icon-gl");
        this.typeIconElem.dataset.icon = "configdrift.camera";
        this.typeContainerElem.appendChild(this.typeIconElem);

        this.typeContainerAOElem = document.createElement("icon-container-gl");
        this.typeIconAOElem = document.createElement("base-icon-gl");
        this.typeIconAOElem.dataset.icon = "configdrift.camera";
        this.typeContainerAOElem.appendChild(this.typeIconAOElem);
        this.typeContainerAOElem.classList.add("hidden");

        this._optionsContainerElem.appendChild(this.typeContainerElem);
        this._optionsContainerElem.appendChild(this.typeContainerAOElem);
        this._optionsContainerElem.appendChild(this._optionsSnapshotLabelElem);
        this._fileNamer = FileName;
        this._rawConfig = "";
        this.obj = {};

        this._commandKeyDown = false;
        this._configViewMain = document.createElement("config-view-main");
        this._tree = document.createElement("tree-view");
        this._tree.classList.add("hidden");
        this._tree.style.width =
            (this._model._userDataStorage.getData("treeContainerWidth", true) || 300) + "px";

        this._resizer = document.createElement("div");
        this._resizer.className = "resizer";
        this._resizer.id = "resizer";
        this._documentWidth = document.body.clientWidth;
        this._resizer.style.width = (this._documentWidth * 0.3) / 100 + "px";

        this._configContainerElem = document.createElement("config-container-gl");
        this._configViewMain.appendChild(this._tree);
        this._configViewMain.appendChild(this._resizer);
        this._configContainerRightElem = document.createElement("config-container-right-gl");
        this._configContainerRightElem.appendChild(this._optionsContainerElem);
        this._configContainerRightElem.appendChild(this._configContainerElem);
        this._configViewMain.appendChild(this._configContainerRightElem);
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._optionsComboBox.on(
            "UserValue",
            (this._onComboBoxUserValue = this._onComboBoxUserValue.bind(this))
        );
        this.model.on(
            "DeviceConfigOptionSelected",
            (this._onSelectConfig = this._onSelectConfig.bind(this))
        );
        this.model.on(
            "DeviceConfAdded",
            (this._onDeviceConfAdded = this._onDeviceConfAdded.bind(this))
        );
        this.model.on(
            "DeviceConfRemoved",
            (this._onDeviceConfRemoved = this._onDeviceConfRemoved.bind(this))
        );
        this.model.on(
            "DeviceConfUpdated",
            (this._onDeviceConfUpdated = this._onDeviceConfUpdated.bind(this))
        );
        this._rootElem.addEventListener("keydown", (this._onKeyDown = this._onKeyDown.bind(this)));
        this._rootElem.addEventListener("keyup", (this._onKeyUp = this._onKeyUp.bind(this)));

        this._resizer.addEventListener(
            "mousedown",
            (this._onResizerMousedown = this._onResizerMousedown.bind(this))
        );
        window.addEventListener(
            "resize",
            (this._onWindowResize = () => {
                this._documentWidth = document.body.clientWidth;
                this._tree.style.width =
                    (this._model._userDataStorage.getData("treeContainerWidth", true) || 200) +
                    "px";
                const bottomBar = this._model._gameEditor._viewActionsCmpnt._renderer._rootElem;

                bottomBar.style.marginLeft =
                    this._tree.offsetWidth + this._resizer.clientWidth + "px";
            })
        );

        this.snapshotTypeComboBox.on(
            "UserValue",
            (this._snapshotTypeChange = this._snapshotTypeChange.bind(this))
        );

        this._model.on("Data", (this._dataUpdate = this._dataUpdate.bind(this)));
    }

    _unregisterListeners() {
        this._optionsComboBox.removeListener("UserValue", this._onComboBoxUserValue);
        this.model.removeListener("DeviceConfigOptionSelected", this._onSelectConfig);
        this.model.removeListener("DeviceConfAdded", this._onDeviceConfAdded);
        this.model.removeListener("DeviceConfRemoved", this._onDeviceConfRemoved);
        this.model.removeListener("DeviceConfUpdated", this._onDeviceConfUpdated);
        this._rootElem.removeEventListener("keydown", this._onKeyDown);
        this._rootElem.removeEventListener("keyup", this._onKeyUp);
        this._resizer.removeEventListener("mousedown", this._onResizerMousedown);
        window.removeEventListener("resize", this._onWindowResize);
        this.snapshotTypeComboBox.removeListener("UserValue", this._snapshotTypeChange);
        this._model.removeListener("Data", this._dataUpdate);
    }

    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Restore",
                name: "RestoreBackup",
                handler: (this._onRestoreBackup = this._onRestoreBackup.bind(this)),
            });
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadConfig",
                handler: (this._onDownloadConfig = this._onDownloadConfig.bind(this)),
            });
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: this._model._viewType === "text" ? "JSON" : "Text",
                name: "switchView",
                position: "left",
                handler: (this._switchViewType = this._switchViewType.bind(this)),
            });
            this._viewActionsAdded = true;
        }

        this._showHideRestoreButton(_.get(this._model, "selectedDeviceConfigOption.id"));
    }

    async _onDownloadConfig() {
        this.model.gameEditor.disableViewAction("DownloadConfig");

        try {
            let rawConfig,
                fileType = "text/plain",
                fileExt = "txt";

            // Use _rawConfig if available, otherwise fall back to _rawConfigCollection for large configs
            rawConfig = this._rawConfig;

            // For large configs, _rawConfig is empty so we need to get it from _rawConfigCollection
            if (_.isEmpty(rawConfig) || rawConfig === "") {
                const selectedConfig = this.model.getSelectedDeviceConfigOption();
                const configId = _.get(selectedConfig, "id");
                let storedRawConfig = this._model._rawConfigCollection[configId];

                // If not in collection, fetch it (for large configs that weren't loaded)
                if (!storedRawConfig && this._model._isConfigTooLarge) {
                    const config = this._model._deviceConfigCollection[configId];
                    if (config) {
                        // Show loading veil while fetching large config
                        this._model._modalUserFeedback.displayLoading({
                            message: "Downloading configuration...",
                            notDismissable: false,
                        });

                        try {
                            storedRawConfig = await this._model.loadRawConfiguration(config);
                            this._model._rawConfigCollection[configId] = storedRawConfig;
                        } catch (err) {
                            this._model._logger.error(
                                {error: err, configId: configId},
                                "Failed to load large config for download"
                            );
                        } finally {
                            this._model._modalUserFeedback.hideDisplayed();
                        }
                    }
                }

                if (storedRawConfig) {
                    // Extract the config string from the stored rawConfig object
                    rawConfig = this._getRootConfig(storedRawConfig.config, "responseStr");
                }
            }

            if (!_.isEmpty(rawConfig)) {
                const selectedConfig = this.model.getSelectedDeviceConfigOption();
                // const selectedConfigName = _.get(selectedConfig, "fileName", "");
                // const specAddendum = selectedConfigName.slice(selectedConfigName.indexOf('('));
                const deviceName = this.model._device.name;
                const date = _.get(selectedConfig, "timestamp", new Date());
                const fileName = this._fileNamer({
                    solution: "DM",
                    general: "DeviceConfig",
                    specific: deviceName,
                    date: date,
                    suffix: "zip",
                });

                let fileConfig = {};
                fileConfig[fileName.slice(0, -4) + "." + fileExt] = {
                    type: fileType,
                    content: rawConfig,
                };
                return this.model.io.fileFactory.saveStringsAsZip(fileConfig, fileName);
            }
        } finally {
            this.model.gameEditor.enableViewAction("DownloadConfig");
        }
    }

    _onRestoreBackup() {
        const latestConfigEntity = this._model._deviceConfigFeed.getInstanceById(
            _.get(this._model._selectedDeviceConfigOption, "id", "")
        );
        const backupConfigId = _.get(
            latestConfigEntity,
            "pristineProperties.backupConfigurationId",
            null
        );
        this._model._gameEditor.setActiveView(
            "App.GameConsole.Section.GameCenter.Editors.DeviceInstanceEditor.Views.Restore",
            [],
            {backupConfigId: backupConfigId}
        );
    }

    _switchViewType() {
        this._model._modalUserFeedback.displayLoading();
        let viewType = this._model._viewType;
        if (viewType === "text") {
            this._model._viewType = "json";
            this._model._userDataStorage.setData("json", "viewType");
        } else {
            this._model._viewType = "text";
            this._model._userDataStorage.setData("text", "viewType");
        }
        this._renderConfig(this._model._selectedPath);
        this._model._gameEditor.setViewActionText(
            "switchView",
            this._model._viewType === "json" ? "Text" : "JSON"
        );
    }

    _onDeviceConfAdded(/*devConfViewModel, */ selectedId) {
        // TODO - Is there an add option to the combobox?
        this._renderOptions(selectedId);
    }

    _onDeviceConfRemoved(/*devConfViewModel*/) {
        // TODO - Is there a remove option to the combobox?
        this._renderOptions();
    }

    _onDeviceConfUpdated(/*devConfViewModel, */ selectedId) {
        // TODO - Is there an update option to the combobox?
        this._renderOptions(selectedId);
    }

    _onSelectConfig() {
        this._model._modalUserFeedback.displayLoading();
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        if (
            this._model._solutionName !== "device_manager" &&
            this._model._solutionName !== "data_explorer"
        ) {
            let selectedConfig = this.model.selectedDeviceConfigOption;
            this._optionsComboBox.setValue(selectedConfig);

            if (selectedConfig.isStable) {
                this.typeIconElem.dataset.icon = "common.star";
            } else if (_.isUndefined(selectedConfig.wasDifferentAtCaptureTime)) {
                this.typeIconElem.dataset.icon = "devicemanager.discovered";
            } else {
                this.typeIconElem.dataset.icon = "configdrift.camera";
            }

            this._renderConfigView(this._model._viewType, true);
        } else {
            let selectedConfig = this.model.selectedDeviceConfigOption;

            if (_.isUndefined(selectedConfig.wasDifferentAtCaptureTime)) {
                this.typeIconElem.dataset.icon = "devicemanager.discovered";
            } else {
                this.typeIconElem.dataset.icon = "configdrift.camera";
            }

            this._renderConfigView(this._model._viewType, true);
        }
    }

    async _renderConfigView(viewType, reusePath = false, path = "") {
        // Hide tree if config is too large, show it otherwise
        if (this._model.isConfigTooLarge()) {
            this._tree.classList.add("hidden");
        } else if (this._model._config) {
            this._tree.classList.remove("hidden");
            this._generateTree();
        } else {
            this._tree.classList.add("hidden");
        }

        // No confirmation dialog - just render directly
        this._renderConfig(path, reusePath);
    }

    _renderConfig(path = "", reusePath = false) {
        let rawConfigObj = [];
        if (!reusePath) {
            this._model._selectedPath = path || "";
        } else {
            path = this._model._selectedPath || "";
        }

        if (
            this.snapshotTypeComboBox.getValue() === "audit_only" &&
            _.get(this._model._config, `${path}`) === undefined
        ) {
            path = "";
        } else if (
            this.snapshotTypeComboBox.getValue() === "drift_n_audit" &&
            _.get(this._model._config, `config.${path}`) === undefined
        ) {
            path = "";
        }

        this._setSelection(path);

        let viewType = this._model._viewType;
        const fullConfig = _.get(this._model, "_config.config", _.get(this._model, "_config", {}));

        if (fullConfig) {
            this._addViewActions();
            const confObj = path === "" ? fullConfig : _.get(fullConfig, path);
            // const confObj = path === "" ? this.obj.capture : _.get(this.obj.capture, path);
            if (_.get(confObj, "isObject", false) && !_.isEmpty(confObj)) {
                this._model._gameEditor.showViewAction("switchView");
            } else {
                this._model._gameEditor.hideViewAction("switchView");
                this._model._jsonDiff = false;
                this._model._isJsonView = false;
            }

            // if (viewType) {
            let hasJSON = false;
            if (_.isArray(_.get(fullConfig, path))) {
                hasJSON =
                    _.get(fullConfig, path).filter((i) => _.isUndefined(i.responseObj)).length ===
                    0;
            } else {
                hasJSON = !_.isUndefined(_.get(fullConfig, `${path}.responseObj`));
            }

            if (this.isGuid(path)) {
                this._rawConfig = "";
                this._rawConfig = this._getCommandSetConfig(_.get(fullConfig, path), "responseStr");
            } else if (path === "") {
                this._rawConfig = "";
                this._rawConfig = this._getRootConfig(fullConfig, "responseStr");
            } else {
                if (viewType === "json" && hasJSON) {
                    const splitPath = path.split(".");
                    const commandSetName =
                        _.get(fullConfig, splitPath[0] + ".commandSetName") ||
                        _.get(this._model._gameEditor._commandSetMap, splitPath[0], "Command Set");
                    let label = "";
                    let valueObj = "";
                    if (!_.isUndefined(_.get(fullConfig, `${path}.conceptItemName`))) {
                        label = "Concept Item";
                        valueObj = _.get(fullConfig, `${path}.conceptItemName`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.restSyncItemName`))) {
                        label = "Rest Data Sync Items";
                        valueObj = _.get(fullConfig, `${path}.restSyncItemName`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.getUrl`))) {
                        label = "Raw HTTP GET requests";
                        valueObj = _.get(fullConfig, `${path}.getUrl`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.command`))) {
                        label = "CLI Command";
                        valueObj = _.get(fullConfig, `${path}.command`);
                    } else {
                        label = "Standard";
                        valueObj = "Standard Configuration";
                    }
                    let rawConfigObjString = "";
                    if (_.isArray(_.get(fullConfig, `${path}`))) {
                        rawConfigObj = [];

                        _.get(fullConfig, path).forEach((i, idx) => {
                            if (idx > 0) {
                                rawConfigObjString += "\n\n";
                            }

                            if (label === "Standard") {
                                rawConfigObjString += this._getHeading(true);

                                if (_.get(i, "isObject", false) && !_.isEmpty(i)) {
                                    this._model._gameEditor.showViewAction("switchView");
                                } else {
                                    this._model._gameEditor.hideViewAction("switchView");
                                    this._model._jsonDiff = false;
                                    this._model._isJsonView = false;
                                }
                            } else {
                                rawConfigObjString += this._getHeading(
                                    false,
                                    commandSetName,
                                    label,
                                    valueObj
                                );
                            }

                            rawConfigObj.push(_.get(i, "responseObj"));
                            rawConfigObjString += JSON.stringify(
                                _.get(i, "responseObj", ""),
                                null,
                                2
                            );
                        });
                    } else {
                        if (label === "Standard") {
                            rawConfigObjString += this._getHeading(true);
                        } else {
                            rawConfigObjString += this._getHeading(
                                false,
                                commandSetName,
                                label,
                                valueObj
                            );
                        }
                        rawConfigObjString += JSON.stringify(
                            _.get(fullConfig, `${path}.responseObj`),
                            null,
                            2
                        );
                        rawConfigObj = _.get(fullConfig, `${path}.responseObj`);
                    }
                    this._rawConfig = rawConfigObjString;
                    // if (!_.isEmpty(rawConfigObj)) {
                    //     this._rawConfig = JSON.stringify(rawConfigObj, null, 2);
                    // }
                } else {
                    const splitPath = path.split(".");
                    const commandSetName =
                        _.get(fullConfig, splitPath[0] + ".commandSetName") ||
                        _.get(this._model._gameEditor._commandSetMap, splitPath[0], "Command Set");
                    let label = "";
                    let valueObj = "";
                    if (!_.isUndefined(_.get(fullConfig, `${path}.conceptItemName`))) {
                        label = "Concept Item";
                        valueObj = _.get(fullConfig, `${path}.conceptItemName`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.restSyncItemName`))) {
                        label = "Rest Data Sync Items";
                        valueObj = _.get(fullConfig, `${path}.restSyncItemName`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.getUrl`))) {
                        label = "Raw HTTP GET requests";
                        valueObj = _.get(fullConfig, `${path}.getUrl`);
                    } else if (!_.isUndefined(_.get(fullConfig, `${path}.command`))) {
                        label = "CLI Command";
                        valueObj = _.get(fullConfig, `${path}.command`);
                    } else {
                        label = "Standard";
                        valueObj = "Standard Configuration";
                    }

                    this._rawConfig = "";
                    if (_.isArray(_.get(fullConfig, `${path}`))) {
                        _.get(fullConfig, `${path}`).forEach((i, idx) => {
                            if (idx > 0) {
                                this._rawConfig += "\n\n";
                            }
                            if (label === "Standard") {
                                this._rawConfig += this._getHeading(true);

                                if (_.get(i, "isObject", false) && !_.isEmpty(i)) {
                                    this._model._gameEditor.showViewAction("switchView");
                                } else {
                                    this._model._gameEditor.hideViewAction("switchView");
                                    this._model._jsonDiff = false;
                                    this._model._isJsonView = false;
                                }
                            } else {
                                this._rawConfig += this._getHeading(
                                    false,
                                    commandSetName,
                                    label,
                                    valueObj
                                );
                            }

                            this._rawConfig += _.get(i, "responseStr", "");
                        });
                    } else {
                        if (label === "Standard") {
                            this._rawConfig += this._getHeading(true);
                        } else {
                            this._rawConfig += this._getHeading(
                                false,
                                commandSetName,
                                label,
                                valueObj
                            );
                        }
                        this._rawConfig += _.get(fullConfig, `${path}.responseStr`);
                    }
                }
                // } else {
                //     if(_.isArray(_.get(fullConfig, `${path}`))) {
                //         _.get(fullConfig, `${path}`).forEach(i => {
                //             rawConfig += '\n' + _.get(i, 'responseStr', '');
                //         })
                //     } else {
                //         rawConfig = _.get(fullConfig, `${path}.responseStr`);
                //     }
                // }
            }
        }

        this._rawConfig = _.trim(this._rawConfig);

        while (this._configContainerElem.hasChildNodes()) {
            this._configContainerElem.removeChild(this._configContainerElem.firstChild);
        }
        if (this._model.isConfigTooLarge()) {
            // Config is too large - show message instead of rendering lines
            const messageContainer = document.createElement("div");
            messageContainer.className = "large-config-message";
            const title = document.createElement("span");
            // "Displaying this large comparison may take time. To reduce time, select a specific command or download the comparison file."
            title.innerHTML =
                "Click Download for a text file of the configuration <br />that was too large to display.";

            messageContainer.appendChild(title);
            this._configContainerElem.appendChild(messageContainer);
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
            this._optionsContainerElem.classList.remove("hidden");
        } else if (this._rawConfig && this._rawConfig !== "{}") {
            // Normal config - render lines
            this._gutterContainerElem = document.createElement("gutter-container-gl");
            this._linesContainerElem = document.createElement("lines-container-gl");
            this._configContainerElem.appendChild(this._gutterContainerElem);
            this._configContainerElem.appendChild(this._linesContainerElem);
            if (this._rawConfig) {
                this._rawConfig = this._rawConfig.replace(/</g, "&lt;");
                _.each(this._rawConfig.split(/\n/), (row, idx) => {
                    row = row.replaceAll(/\r/g, "");
                    if (row.search("apic") >= 0) {
                        // specific to apic to remove the long space between, % and apic
                        row = row.replaceAll(/[ ]*apic/g, " apic");
                    }
                    // LINE NUMBER
                    let lineNumElem = document.createElement("line-number-gl");
                    lineNumElem.innerHTML = idx + 1;
                    // LINE
                    let lineElem = document.createElement("line-gl");
                    lineElem.innerHTML = row;
                    // ROW
                    let rowElem = document.createElement("pre");
                    rowElem.classList.add("config-row-gl");
                    rowElem.appendChild(lineElem);
                    this._gutterContainerElem.appendChild(lineNumElem);
                    this._linesContainerElem.appendChild(rowElem);
                });
            }
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
            this._optionsContainerElem.classList.remove("hidden");
        } else {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
            this._optionsContainerElem.classList.add("hidden");
        }

        _.delay(this.hideVeil.bind(this), 100);
    }

    hideVeil() {
        return this._model._modalUserFeedback.hideDisplayed();
    }

    _renderIcon() {
        let selectedConfig = this.model.getSelectedDeviceConfigOption();
        if (selectedConfig) {
            let icon = selectedConfig.isStable ? "common.star" : "configdrift.camera";
            this.typeIconElem.dataset.icon = icon;
        } else {
            // TODO Confirm whether we blank out the icon
        }
    }

    async _renderOptions(select) {
        let configOptions = this.model.getViewModel();
        let selectedItem;
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        if (
            this._model._solutionName === "device_manager" ||
            this._model._solutionName === "data_explorer"
        ) {
            if (!_.isEmpty(configOptions)) {
                let latestConfig = configOptions[0];

                if (!_.isEmpty(select)) {
                    selectedItem = configOptions.find((i) => i.value.id === select);
                    if (!_.isEmpty(selectedItem)) {
                        latestConfig = selectedItem;
                    }
                }

                this._optionsLatestLabelElem.innerText = _.get(latestConfig, "label", "");
                this.typeIconElem.dataset.icon = _.get(latestConfig, "value.isStable", false)
                    ? "common.star"
                    : _.get(latestConfig, "value.fromCapture", false)
                    ? "configdrift.camera"
                    : "devicemanager.discovered";
                await this.model.setSelectedDeviceConfigOption(latestConfig.value, true);
            }
        } else {
            this._optionsComboBox.setOptions(configOptions);

            if (_.isEmpty(configOptions)) {
                this._optionsComboBox.setDisabled(true);
            } else {
                this._optionsComboBox.setDisabled(false);
            }

            let selectedConfig = this.model.getSelectedDeviceConfigOption();

            if (_.isEmpty(selectedConfig) && configOptions.length === 0) {
                // We got nothing, and nothing to select
            } else if (_.isEmpty(selectedConfig)) {
                // Nothing was selected, but we have some options to choose from
                selectedConfig = configOptions[configOptions.length - 1].value;

                await this.model.setSelectedDeviceConfigOption(selectedConfig, true);
                this._optionsComboBox.setValue(selectedConfig);
                this._renderIcon(selectedConfig);
            } else {
                // We have something selected already, so let's use it!
                this._optionsComboBox.setValue(selectedConfig);
                this._renderIcon(selectedConfig);
            }

            if (!_.isEmpty(select)) {
                selectedItem = configOptions.find((i) => i.value.id === select);

                this._optionsComboBox.setValue(selectedItem.value);
            }
        }
    }

    _showHideRestoreButton(configId) {
        const latestConfigEntity = this._model._deviceConfigFeed.getInstanceById(configId);

        if (this._model._gameEditor.canRestoreBackup) {
            this.model.gameEditor.showViewAction("RestoreBackup");
            if (_.get(latestConfigEntity, "pristineProperties.backupConfigurationId", null)) {
                this.model.gameEditor.enableViewAction("RestoreBackup");
            } else {
                this.model._gameEditor.disableViewAction("RestoreBackup");
            }
        } else {
            this.model.gameEditor.hideViewAction("RestoreBackup");
        }
    }

    _onComboBoxUserValue() {
        this.model.setSelectedDeviceConfigOption(this._optionsComboBox.value);
        this._showHideRestoreButton(_.get(this._optionsComboBox.value, "id"));
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        const bottomBar = this._model._gameEditor._viewActionsCmpnt._renderer._rootElem;
        bottomBar.removeAttribute("style");
        this._model._gameEditor._renderer._rootElem.classList.remove("config-view-game-editor");
        let promArr = [this._optionsComboBox.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._optionsComboBox = undefined;
                this._optionsContainerElem.remove();
                this._optionsContainerElem = undefined;
                this._configContainerElem.remove();
                this._configContainerElem = undefined;
                this._defaultViewActive = undefined;
                this._commandKeyDown = undefined;
                this._model = undefined;
                this._tree.remove();
                this._tree = undefined;
                this._resizer.remove();
                this._resizer = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._rawConfig = undefined;
            },
            (err) => {
                this.model._logger.warn({error: err}, "ConfigViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
            // To be refactored with separate views once form layout / style specifications have matured
            if (
                this._model._solutionName === "device_manager" ||
                this._model._solutionName === "data_explorer"
            ) {
                this._optionsContainerElem.appendChild(this._optionsLatestLabelElem);
            } else {
                this._optionsContainerElem.appendChild(this._optionsComboBox.renderer.rootElement);
            }

            this._rootElem.appendChild(this._configViewMain);
        }
        this._model._modalUserFeedback.displayLoading();
        return this.model.whenModelIsReady().then(async () => {
            this._renderOptions();

            const initialPath = "";

            await this._renderConfigView(this._model._viewType, false, initialPath);
            this._rootElem.focus();
            const bottomBar = this._model._gameEditor._viewActionsCmpnt._renderer._rootElem;
            bottomBar.style.marginLeft = this._tree.offsetWidth + this._resizer.clientWidth + "px";
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    //TODO Private Render Methods here

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Catch the keydown event
     * @param event {Event}
     * @private
     */
    _onKeyDown(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = true;
        }
        // Command/CTRL + a/A
        if ((event.ctrlKey === true || this._commandKeyDown === true) && event.keyCode === 65) {
            event.preventDefault();
            if (document.selection) {
                let range = document.body.createTextRange();
                range.moveToElementText(this._linesContainerElem);
                range.select();
            } else if (window.getSelection) {
                let range = document.createRange();
                range.selectNode(this._linesContainerElem);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
            }
        }
    }

    /**
     * Catch the keyup event
     * @param event {Event}
     * @private
     */
    _onKeyUp(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = false;
        }
    }

    _onResizerMousedown(e) {
        let treeWidth = this._tree.style.width;
        const bottomBar = this._model._gameEditor._viewActionsCmpnt._renderer._rootElem;
        e = e || window.event;
        let start = 0,
            diff = 0;
        if (e.pageX) {
            start = e.pageX;
        } else if (e.clientX) {
            start = e.clientX;
        }
        document.body.onmousemove = (e) => {
            e = e || window.event;
            let end = 0;
            if (e.pageX) {
                end = e.pageX;
            } else if (e.clientX) {
                end = e.clientX;
            }
            diff = end - start;
            let updatedTreeWidth = this._tree.style.width;
            if (parseInt(updatedTreeWidth, 10) > 100 || diff > 0) {
                this._tree.style.width = parseInt(treeWidth, 10) + diff + "px";
                bottomBar.style.marginLeft =
                    this._tree.offsetWidth + this._resizer.clientWidth + "px";
            }
        };
        document.body.onmouseup = () => {
            this._model._userDataStorage.setData(
                parseInt(this._tree.style.width, 10),
                "treeContainerWidth"
            );
            bottomBar.style.marginLeft = this._tree.offsetWidth + this._resizer.clientWidth + "px";
            document.body.onmousemove = document.body.onmouseup = null;
        };
    }

    _snapshotTypeChange(val) {
        this._model._updateJsonData(val);
        // typeContainerAOElem
    }

    _dataUpdate() {
        this._model._modalUserFeedback.displayLoading();
        this._renderConfigView();

        if (this._model._selectedView === "drift_n_audit") {
            this.typeContainerAOElem.classList.add("hidden");
            this._optionsSnapshotLabelElem.classList.add("hidden");
            this.typeContainerElem.classList.remove("hidden");
            this._optionsLatestLabelElem.classList.remove("hidden");
            this._optionsComboBox._renderer._rootElem.classList.remove("hidden");
        } else {
            if (this._model._configName) {
                this._optionsSnapshotLabelElem.innerHTML = this._model._configName;
                this.typeContainerAOElem.classList.remove("hidden");
                this._optionsSnapshotLabelElem.classList.remove("hidden");
            }
            this.typeContainerElem.classList.add("hidden");
            this._optionsLatestLabelElem.classList.add("hidden");
            this._optionsComboBox._renderer._rootElem.classList.add("hidden");
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message = "No snapshot available";
        if (this._model._config) {
            if (this._model._selectedView === "drift_n_audit") {
                message = "No configuration snapshot available";
            } else if (this._model._selectedView === "audit_only") {
                message = "No operational snapshot available";
            } else if (this._model.isConfigTooLarge()) {
                message =
                    "Click Download for a text file of the configuration <br />that was too large to display.";
            }
        }

        this._rootElem.parentNode.classList.add("no-config");

        this._model.gameEditor
            .showDefaultMessageView({
                message,
            })
            .then(() => {
                this._defaultViewActive = true;
            });
    }

    _hideDefaultMessageDisplay() {
        this._rootElem.parentNode.classList.remove("no-config");
        this._model.gameEditor.hideDefaultMessageView().then(() => {
            this._defaultViewActive = false;
        });
    }

    // Tree Code - Start
    _generateTree() {
        const rootKey = "capture";

        this.obj = {
            capture: _.get(this._model, "_config.config", _.get(this._model, "_config", {})),
        };

        const rootNode = this.buildTree(this.obj[rootKey], rootKey);
        this._tree.innerHTML = "";

        this.snapshotTypeComboBox.setValue(this._model._selectedView);

        this._tree.appendChild(this.snapshotTypeComboBox._renderer._rootElem);
        this._tree.appendChild(rootNode);
        // if (_.get(this.obj, `${rootKey}.basicMode`)) {
        //     this._updateConfiguration("basicMode");
        //     this._setSelection("basicMode");
        // } else {
        //     const firstNodeContainer = this._tree.querySelectorAll(".node-class")[0];
        //     const firstNode = firstNodeContainer.querySelector(".tree-node");
        //     const pathToFirstNode = firstNode.getAttribute("data-path");
        //     if (pathToFirstNode) {
        //         this._updateConfiguration(pathToFirstNode);
        //         this._setSelection(pathToFirstNode);
        //     }
        // }
        // // Expand/Collapse functionality
        const treeNodes = this._tree.querySelectorAll(".tree-node");
        treeNodes.forEach((node) => {
            const toggleButton = node.querySelector(".toggle-button");
            if (toggleButton) {
                toggleButton.addEventListener("click", (e) => {
                    e.stopImmediatePropagation();
                    node.classList.toggle("collapsed");
                });
            }
            node.addEventListener("click", (e) => {
                e.stopImmediatePropagation();
                const path = e.currentTarget.getAttribute("data-path");
                this._renderConfig(path);
                this._setSelection(path);
            });
        });
    }
    createTreeNode(label, className = "", type = undefined, path = "", level) {
        if (label) {
            const node = document.createElement("div");
            node.className = "tree-node " + className;
            node.setAttribute("data-path", path);
            if (type !== "node") {
                const toggleButton = document.createElement("button");
                toggleButton.className = "toggle-button";
                node.appendChild(toggleButton);
            }
            const span = document.createElement("span");
            if (level === "root") {
                const _iconRoot = document.createElement("base-icon-gl");
                _iconRoot.dataset.icon = "configdrift.snapshot";
                _iconRoot.classList.add("icon", "m-r-4");
                span.appendChild(_iconRoot);
            } else if (level === "folder" && label !== "Standard") {
                const _iconFolderOpen = document.createElement("base-icon-gl");
                _iconFolderOpen.dataset.icon = "fileserver.folder";
                _iconFolderOpen.classList.add("icon-open", "m-r-4");

                const _iconFolderClose = document.createElement("base-icon-gl");
                _iconFolderClose.dataset.icon = "fileserver.folder_open";
                _iconFolderClose.classList.add("icon-close", "m-r-4");

                span.appendChild(_iconFolderOpen);
                span.appendChild(_iconFolderClose);
            } else {
                const _iconFile = document.createElement("base-icon-gl");
                _iconFile.dataset.icon = "fileserver.file";
                _iconFile.classList.add("icon");

                span.appendChild(_iconFile);
            }

            const spanText = document.createElement("span");
            spanText.innerText = label;
            span.appendChild(spanText);
            node.appendChild(span);

            return node;
        }
    }

    _isEmptyCommandSet(obj) {
        for (const key in obj) {
            if (
                key !== "commandSetName" &&
                obj.hasOwnProperty(key) &&
                Array.isArray(obj[key]) &&
                obj[key].length > 0
            ) {
                return false; // At least one non-empty array found
            }
        }
        return true; // All arrays are empty or no arrays found
    }

    buildTree(node, key) {
        const diffClass = "node";
        let label = key;
        if (key === "capture") {
            label = "All";
        }
        const treeNode = this.createTreeNode(label, diffClass, undefined, undefined, "root");
        if (typeof this.obj[key] === "object") {
            const subKeys = new Set([...Object.keys(this.obj[key])]);
            for (const subKey of subKeys) {
                if (subKey === "isObject") {
                    continue;
                }
                let labelVal = subKey;
                if (subKey === "basicMode") {
                    labelVal = "Standard";
                }
                if (this.isGuid(subKey)) {
                    labelVal =
                        _.get(this.obj, `capture.${subKey}.commandSetName`) ||
                        _.get(this._model._gameEditor._commandSetMap, subKey, "Command Set");
                }
                const subDiffClass = "node";

                if (
                    typeof this.obj[key][subKey] === "object" &&
                    !_.isArray(this.obj[key][subKey])
                ) {
                    if (this._isEmptyCommandSet(this.obj[key][subKey])) {
                        continue;
                    }
                }

                const subNode = this.createTreeNode(
                    labelVal,
                    `${subDiffClass} ${subKey === "basicMode" ? "basic-node" : ""}`,
                    subKey === "basicMode" ? "node" : undefined,
                    `${subKey}`,
                    "folder"
                );
                if (Array.isArray(this.obj[key][subKey])) {
                    const subTree = this.buildSubTree(this.obj[key][subKey], subKey, subNode);
                    subNode.appendChild(subTree);
                } else if (typeof this.obj[key][subKey] === "object") {
                    const subSubTree = this.buildSubTree(
                        _.get(this.obj, `${key}.${subKey}`, undefined),
                        subKey,
                        subNode
                    );
                    subNode.appendChild(subSubTree);
                }
                treeNode.appendChild(subNode);
            }
        }
        return treeNode;
    }
    buildSubTree(subObject, subKey, subNodePassed) {
        const subTreeContainer = document.createElement("div");
        if (Array.isArray(subObject)) {
            for (let i = 0; i < Math.max(_.get(subObject, "length", 0)); i++) {
                const item = _.get(subObject, i);
                const diffClass = "node";
                if (item && item.command) {
                    const subNode = this.createTreeNode(item && item.command, diffClass, "folder");
                    subTreeContainer.appendChild(subNode);
                }
            }
        } else if (typeof subObject === "object") {
            const subKeys = new Set([..._.keys(subObject)]);
            for (const subSubKey of subKeys) {
                if (!_.isEmpty(_.get(this.obj, `capture.${subKey}.${subSubKey}`))) {
                    let selector = "";
                    if (subSubKey === "cliCommands") {
                        selector = "command";
                    } else if (subSubKey === "conceptItems") {
                        selector = "conceptItemName";
                    } else if (subSubKey === "getUrls") {
                        selector = "getUrl";
                    } else if (subSubKey === "restSyncItems") {
                        selector = "restSyncItemName";
                    }
                    if (
                        !_.isEmpty(_.get(subObject, subSubKey, [])) &&
                        _.isArray(_.get(subObject, subSubKey, []))
                    ) {
                        const subSubTreeContainer = document.createElement("div");
                        subSubTreeContainer.classList.add("node-class");
                        _.get(subObject, subSubKey, []).forEach((item, i) => {
                            const diffClass = "node";
                            const subNode = this.createTreeNode(
                                item[selector] || subSubKey,
                                diffClass,
                                "node",
                                `${subKey}.${subSubKey}.${i}`,
                                "node"
                            );
                            subSubTreeContainer.appendChild(subNode);
                        });
                        subNodePassed.appendChild(subSubTreeContainer);
                    }
                }
            }
        }
        return subTreeContainer;
    }

    isGuid(str) {
        const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return guidPattern.test(str);
    }

    _setSelection(path) {
        this._selectedPath = path;
        const treeNodes = this._tree.querySelectorAll(".tree-node");
        treeNodes.forEach((node) => {
            node.classList.remove("selected");
        });
        const selectedNode = this._tree.querySelector(".tree-node[data-path='" + path + "']");
        if (selectedNode) {
            selectedNode.classList.add("selected");
        }
    }

    _getCommandSetConfig(obj1, viewType) {
        let _conf = "";
        if (_.isObject(obj1)) {
            Object.entries(obj1 || {}).forEach(([lab, val]) => {
                if (!_.isEmpty(val)) {
                    let label = "";
                    let valueObj = "";
                    if (lab === "cliCommands") {
                        label = "CLI Command";
                        valueObj = "command";
                    } else if (lab === "conceptItems") {
                        label = "Concept Item";
                        valueObj = "conceptItemName";
                    } else if (lab === "getUrls") {
                        label = "Raw HTTP GET request";
                        valueObj = "getUrl";
                    } else if (lab === "restSyncItems") {
                        label = "Rest Data Sync Item";
                        valueObj = "restSyncItemName";
                    }

                    if (_.isArray(val)) {
                        val.forEach((item) => {
                            const commandSetName =
                                _.get(obj1, "commandSetName", false) ||
                                _.get(this._model._gameEditor._commandSetMap, obj1, "Command Set");

                            _conf += this._getHeading(false, commandSetName, label, item[valueObj]);

                            _conf += _.get(item, viewType, "");
                        });
                    }
                }
            });
        }
        return _.trim(_conf);
    }
    _getRootConfig(obj1, viewType) {
        let _conf = "";
        // Config for Object 1
        if (obj1 && _.isObject(obj1)) {
            if (_.get(obj1, "basicMode")) {
                // Standard Config
                _conf += this._getHeading(true);
                obj1.basicMode.forEach((i) => {
                    _conf += _.get(i, viewType);
                });
            }
            Object.entries(obj1).forEach(([labCs, valCs]) => {
                if (labCs !== "basicMode") {
                    if (this.isGuid(labCs)) {
                        //Command Set Name

                        Object.entries(valCs || {}).forEach(([lab, val]) => {
                            if (!_.isEmpty(val)) {
                                let label = "";
                                let valueObj = "";
                                if (lab === "cliCommands") {
                                    label = "CLI Command";
                                    valueObj = "command";
                                } else if (lab === "conceptItems") {
                                    label = "Concept Item";
                                    valueObj = "conceptItemName";
                                } else if (lab === "getUrls") {
                                    label = "Raw HTTP GET request";
                                    valueObj = "getUrl";
                                } else if (lab === "restSyncItems") {
                                    label = "Rest Data Sync Item";
                                    valueObj = "restSyncItemName";
                                }

                                if (_.isArray(val)) {
                                    val.forEach((item) => {
                                        const commandSetName =
                                            _.get(valCs, "commandSetName", false) ||
                                            _.get(
                                                this._model._gameEditor._commandSetMap,
                                                labCs,
                                                "Command Set"
                                            );
                                        _conf += this._getHeading(
                                            false,
                                            commandSetName,
                                            label,
                                            item[valueObj]
                                        );

                                        _conf += _.get(item, viewType, "");
                                    });
                                }
                            }
                        });
                    }
                }
            });
        }

        return _.trim(_conf);
    }

    _getHeading(standard, commandSetName, label, valueObj) {
        let str = "";
        if (standard) {
            str +=
                "**************************************************************************************\n";
            str += "Standard Configuration";
            str +=
                "\n**************************************************************************************\n\n";
        } else {
            str += "\n\n\n";

            str +=
                "**************************************************************************************\n";
            str += "Command Set : " + commandSetName;
            str += "\n" + label + " : " + valueObj;
            str +=
                "\n**************************************************************************************\n\n";
        }

        return str;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ConfigViewRenderer;
